import api from '../api/client';

/**
 * Authenticated file download helper.
 *
 * Why this exists: opening a backend URL via `<a target="_blank">` or
 * `window.open` strips Authorization headers and (on cross-origin preview
 * deploys) doesn't reliably send HttpOnly cookies either, so the backend
 * returns 401 — which some edge proxies wrap as a plain "404 page not found"
 * page. Use this helper for ALL CSV / PDF / XLSX downloads from the API.
 *
 * Flow: GET the URL through our authenticated axios client → wrap response
 * in a Blob → spawn a hidden <a download> → click it → revoke the blob URL.
 *
 * @param {string} path API path relative to /api/v1 (e.g. "/question-banks/csv-template")
 * @param {string} filename Filename the browser should save as
 * @param {object} [opts]
 * @param {string} [opts.mime="text/csv"] Content-Type fallback if response omits one
 * @returns {Promise<void>} Rejects on HTTP error (toast or rethrow upstream)
 */
export async function downloadAuthedFile(path, filename, opts = {}) {
  const { mime = 'text/csv' } = opts;
  const res = await api.get(path, { responseType: 'blob' });
  // axios sets blob's type from response Content-Type if present; fall back
  // to the caller's hint for older proxies that strip it.
  const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Give the browser one tick to start the download before tearing down the
  // blob URL. Some Safari builds otherwise cancel the save dialog.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
